import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import i18n from 'i18next';
import { createBrowserRouter, RouterProvider, redirect, Navigate, useNavigate } from "react-router-dom";
import TagManager from 'react-gtm-module'
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from "react-i18next";

import './i18n';
import './index.css';
import Root from './components/core/Root';
import Home from './page/Home';
import ApplyNew from './page/ApplyNew';
import ApplicationProgress from './page/ApplicationProgress';
import ApplicationSupplementary from './page/ApplicationSupplementary';
import AdminApplication from './page/AdminApplication';
import AdminAccount from './page/AdminAccount';
import AdminApplicationDetail from './page/AdminApplicationDetail';
import AdminSetting from './page/AdminSetting';
import AdminSignIn from './page/AdminSignIn';
import NotFound from './page/NotFound';
import Sidebar from './components/admin/Sidebar';
import reportWebVitals from './reportWebVitals';
import { basename, enableIntranetPath, enableInternetPath } from './setting';
import useAuthAdminStore from './store/useAuthAdminStore';
import useApplyNewStore from './store/useApplyNewStore';
import { gtmId } from './setting';

const ProtectedRoute = ({ children, accountPage = false }) => {
  const { isAuthenticated, isAdminUser } = useAuthAdminStore();

  if (!isAuthenticated()) {
    return <Navigate to={`/${basename}/${i18n.language}/admin/signin`} replace />;
  }

  if (accountPage && !isAdminUser()) {
    return <Navigate to={`/${basename}/${i18n.language}/admin/application`} replace />;
  }

  return children;
};

const RedirectToDashboardIfLoggedIn = ({ children }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthAdminStore();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(`/${basename}/${i18n.language}/admin/application`); // Adjust the path as needed
    }
  }, [navigate]);

  return children;
};

const frontendPath = [
  {
    path: `/${basename}`,
    loader: async () => redirect(`/${basename}/${i18n.language}`),
  },
  {
    path: `/${basename}/en`,
    element: <Home />,
  },
  {
    path: `/${basename}/ch`,
    element: <Home />,
  },
  {
    path: `/${basename}/en/apply`,
    element: <ApplyNew />,
  },
  {
    path: `/${basename}/ch/apply`,
    element: <ApplyNew />,
  },
  {
    path: `/${basename}/en/apply/success`,
    loader: async () => {
      const { setStep, refno } = useApplyNewStore.getState();

      if (refno !== "") {
        setStep("success");
        return null;
      } else {
        return redirect(`/${basename}/${i18n.language}/apply`);
      }
    },
    element: <ApplyNew />,
  },
  {
    path: `/${basename}/ch/apply/success`,
    loader: async () => {
      const { setStep, refno } = useApplyNewStore.getState();

      if (refno !== "") {
        setStep("success");
        return null;
      } else {
        return redirect(`/${basename}/${i18n.language}/apply`);
      }
    },
    element: <ApplyNew />,
  },
  {
    path: `/${basename}/en/apply/progress`,
    element: <ApplicationProgress />,
  },
  {
    path: `/${basename}/ch/apply/progress`,
    element: <ApplicationProgress />,
  },
  {
    path: `/${basename}/en/apply/supplementary/:emailId`,
    element: <ApplicationSupplementary />,
  },
  {
    path: `/${basename}/ch/apply/supplementary/:emailId`,
    element: <ApplicationSupplementary />,
  },
];

const intranetPath = [
  {
    path: `/${basename}/admin`,
    loader: async () => {
      return redirect(`/${basename}/${i18n.language}/admin/signin`);
    },
  },
  {
    path: `/${basename}/en/admin/signin`,
    element: <RedirectToDashboardIfLoggedIn><AdminSignIn /></RedirectToDashboardIfLoggedIn>,
  },
  {
    path: `/${basename}/ch/admin/signin`,
    element: <RedirectToDashboardIfLoggedIn><AdminSignIn /></RedirectToDashboardIfLoggedIn>,
  },
  {
    element: <Sidebar />,
    children: [
      {
        path: `/${basename}/en/admin/application`,
        element: <ProtectedRoute><AdminApplication /></ProtectedRoute>,
      },
      {
        path: `/${basename}/ch/admin/application`,
        element: <ProtectedRoute><AdminApplication /></ProtectedRoute>,
      },
      {
        path: `/${basename}/en/admin/application/:aid`,
        element: <ProtectedRoute><AdminApplicationDetail /></ProtectedRoute>,
      },
      {
        path: `/${basename}/ch/admin/application/:aid`,
        element: <ProtectedRoute><AdminApplicationDetail /></ProtectedRoute>,
      },
      {
        path: `/${basename}/en/admin/account`,
        element: <ProtectedRoute accountPage={true}><AdminAccount /></ProtectedRoute>,
      },
      {
        path: `/${basename}/ch/admin/account`,
        element: <ProtectedRoute accountPage={true}><AdminAccount /></ProtectedRoute>,
      },
      {
        path: `/${basename}/en/admin/setting`,
        element: <ProtectedRoute><AdminSetting /></ProtectedRoute>,
      },
      {
        path: `/${basename}/ch/admin/setting`,
        element: <ProtectedRoute><AdminSetting /></ProtectedRoute>,
      },
    ],
  },
];

const notFoundPath = [
  {
    path: '*',
    element: <NotFound />,
  },
];

const router = createBrowserRouter([
  {
    path: `/${basename}`,
    element: <Root />,
    basename: `/${basename}`,
    children: [
      ...(enableInternetPath ? frontendPath : []),
      ...(enableIntranetPath ? intranetPath : []),
      ...notFoundPath,
    ],
  },
]);

if (gtmId) {
  TagManager.initialize({ gtmId });
}

const Head = () => {
  const { t } = useTranslation();
  useEffect(() => {
    if (process.env.REACT_APP_CLARITY) {
      (function(c, l, a, r, i, t, y) {
        c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments) };
        t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      })(window, document, "clarity", "script", process.env.REACT_APP_CLARITY);
    }
  }, []);
  
  return (
    <Helmet>
      <title>{t("translation:home.document_title")}</title>
      <meta name="description" content={t("translation:home.document_description")} />
      <meta name="keywords" content={t("translation:home.document_keyword")}></meta>
      <meta property="og:title" content={t("translation:home.document_title")}></meta>
      <meta property="og:description" content={t("translation:home.document_description")}></meta>
      <meta property="og:image" content="https://www.bluecross.com.hk/images/media/share_home_logo.jpg"></meta>
    </Helmet>
  )
}


// // TODO mobile APP 隐藏header 测试数据
// //@ts-ignore :IOSMobileApp 由IOS mobile app 内置
// window.IOSMobileApp = true;


const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <Head />
      <RouterProvider router={router} />
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
